import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import type { MfaConfirmBody, RespondentLoginBody, RespondentRegisterBody } from "@/api/types";

export const ME_QUERY_KEY = ["r", "auth", "me"] as const;

export function useCurrentRespondent() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RespondentLoginBody) => authApi.login(body),
    onSuccess: (result) => {
      // The session is live only when MFA was NOT required (ADR-0043): then the
      // account is present. When mfa_required, the caller routes to /mfa.
      if (!result.mfa_required && result.account) {
        qc.setQueryData(ME_QUERY_KEY, result.account);
      }
    },
  });
}

export function useRegisterMutation() {
  // Register issues NO session (ADR-0043) — leave ME untouched; the screen
  // routes to /verify with the returned verify_ticket.
  return useMutation({
    mutationFn: (body: RespondentRegisterBody) => authApi.register(body),
  });
}

export function useMfaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MfaConfirmBody) => authApi.loginMfa(body),
    onSuccess: (account) => qc.setQueryData(ME_QUERY_KEY, account),
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.clear();
    },
  });
}
